import React, { useEffect, useCallback, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Fullscreen image lightbox.
 * - Renders into document.body via portal so it always sits above the page chrome
 * - Touch swipe support on mobile, ESC + arrows on desktop
 * - Locks body scroll without scroll-jumping the page
 * - Uses dynamic viewport (svh) so mobile address bars don't crop the image
 */
export const Lightbox = ({ images, index, onClose, onPrev, onNext }) => {
  const [loaded, setLoaded] = useState(false);
  const touchStartX = useRef(null);
  const touchStartY = useRef(null);

  const handleKey = useCallback(
    (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") onPrev();
      if (e.key === "ArrowRight") onNext();
    },
    [onClose, onPrev, onNext]
  );

  // Lock body scroll without losing scroll position
  useEffect(() => {
    const scrollY = window.scrollY;
    const body = document.body;
    const prev = {
      position: body.style.position,
      top: body.style.top,
      width: body.style.width,
      overflow: body.style.overflow,
    };
    body.style.position = "fixed";
    body.style.top = `-${scrollY}px`;
    body.style.width = "100%";
    body.style.overflow = "hidden";

    document.addEventListener("keydown", handleKey);

    return () => {
      document.removeEventListener("keydown", handleKey);
      body.style.position = prev.position;
      body.style.top = prev.top;
      body.style.width = prev.width;
      body.style.overflow = prev.overflow;
      window.scrollTo(0, scrollY);
    };
  }, [handleKey]);

  // Reset loaded state on image change
  useEffect(() => {
    setLoaded(false);
  }, [index]);

  const onTouchStart = (e) => {
    const t = e.touches[0];
    touchStartX.current = t.clientX;
    touchStartY.current = t.clientY;
  };
  const onTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - touchStartX.current;
    const dy = t.clientY - touchStartY.current;
    touchStartX.current = null;
    touchStartY.current = null;

    // Vertical down-swipe → close
    if (Math.abs(dy) > 80 && Math.abs(dy) > Math.abs(dx) && dy > 0) {
      onClose();
      return;
    }
    // Horizontal swipe → prev/next
    if (Math.abs(dx) > 60 && images.length > 1) {
      if (dx < 0) onNext();
      else onPrev();
    }
  };

  if (index < 0 || index >= images.length) return null;

  const modal = (
    <div
      className="fixed inset-0 z-[9999] bg-black/95 backdrop-blur-sm flex items-center justify-center"
      onClick={onClose}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      data-testid="lightbox"
      role="dialog"
      aria-modal="true"
      style={{
        height: "100dvh",
        width: "100vw",
        paddingTop: "env(safe-area-inset-top, 0px)",
        paddingBottom: "env(safe-area-inset-bottom, 0px)",
        paddingLeft: "env(safe-area-inset-left, 0px)",
        paddingRight: "env(safe-area-inset-right, 0px)",
      }}
    >
      {/* Close */}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        data-testid="lightbox-close"
        className="absolute top-4 right-4 md:top-5 md:right-5 w-11 h-11 rounded-full bg-white/15 hover:bg-white/25 active:bg-white/30 text-white flex items-center justify-center transition-colors z-10"
        aria-label="Close"
        style={{ touchAction: "manipulation" }}
      >
        <X className="w-5 h-5" />
      </button>

      {/* Prev */}
      {images.length > 1 && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onPrev();
          }}
          data-testid="lightbox-prev"
          className="hidden md:flex absolute left-3 md:left-6 w-11 h-11 rounded-full bg-white/15 hover:bg-white/25 active:bg-white/30 text-white items-center justify-center transition-colors z-10"
          aria-label="Previous"
          style={{ touchAction: "manipulation" }}
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
      )}

      {/* Image wrapper */}
      <div
        className="relative w-full h-full flex items-center justify-center px-4 py-12 md:p-16"
        onClick={(e) => e.stopPropagation()}
      >
        {!loaded && (
          <div
            className="absolute inset-0 flex items-center justify-center pointer-events-none"
            aria-hidden="true"
          >
            <div className="w-8 h-8 rounded-full border-2 border-white/30 border-t-white animate-spin" />
          </div>
        )}
        <img
          src={images[index]}
          alt={`Image ${index + 1} of ${images.length}`}
          onLoad={() => setLoaded(true)}
          className={`max-w-full max-h-full object-contain select-none transition-opacity duration-300 ${
            loaded ? "opacity-100" : "opacity-0"
          }`}
          draggable={false}
          data-testid="lightbox-image"
          style={{ touchAction: "pan-y" }}
        />
      </div>

      {/* Next */}
      {images.length > 1 && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onNext();
          }}
          data-testid="lightbox-next"
          className="hidden md:flex absolute right-3 md:right-6 w-11 h-11 rounded-full bg-white/15 hover:bg-white/25 active:bg-white/30 text-white items-center justify-center transition-colors z-10"
          aria-label="Next"
          style={{ touchAction: "manipulation" }}
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      )}

      {/* Counter + swipe hint */}
      {images.length > 1 && (
        <div className="absolute bottom-5 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-white/15 text-white text-xs font-body font-semibold tracking-widest z-10">
          {index + 1} / {images.length}
          <span className="ml-2 md:hidden opacity-70">· swipe</span>
        </div>
      )}
    </div>
  );

  if (typeof document === "undefined") return null;
  return createPortal(modal, document.body);
};
